<template>
  <div class="bookmarks__course-container" @click.stop="openBookmark()">
    <div class="bookmarks__preview-container">
      <span class="dashboard__badge bookmarks__preview-time">{{
        getBookmarkTimeLabel
      }}</span>
      <img
        :src="videoImgUrl"
        class="bookmarks__preview-img"
        alt="Video Image"
      />
      <PlayIcon class="bookmarks__preview-icon" />
    </div>
    <div class="bookmarks__details">
      <div class="bookmarks__progress-container">
        <div class="dashboard__progress-wrapper bookmarks__progress-wrapper">
          <div
            class="bookmarks__progress-point"
            :style="`left: ${getBookmarkPoint}%`"
          ></div>
        </div>
        <div class="dashboard__icons-wrapper bookmarks__icons-wrapper--mobile">
          <TrashIcon
            @click.stop="deleteBookmark()"
            class="dashboard__action-icon dashboard__icon dashboard__icon--trash"
          />
          <PlayIcon class="dashboard__action-icon dashboard__icon--play" />
        </div>
      </div>
      <div class="bookmarks__bottom-container">
        <div class="bookmarks__notes-wrapper">
          <div class="bookmarks__notes-icon"><NoteIndicator /></div>
          <div class="bookmarks__notes-label">{{ noteLabel }}</div>
        </div>
        <div class="dashboard__icons-wrapper bookmarks__icons-wrapper">
          <TrashIcon
            @click.stop="deleteBookmark()"
            class="dashboard__action-icon dashboard__icon dashboard__icon--trash"
          />
          <PlayIcon class="dashboard__action-icon dashboard__icon--play" />
        </div>
      </div>
    </div>
    <ActionLoader
      v-if="isDeletingBookmark"
      class="dashboard-item__action-loader"
    />
  </div>
</template>

<script>
import { PlayIcon, TrashIcon } from 'vue-feather-icons'
import NoteIndicator from '@/assets/icons/course/note_indicator.svg'
import CONSTANTS from '@/constants'
import ActionLoader from '@/components/shared/ActionLoader.vue'

export default {
  name: 'BookmarkTile',
  components: {
    PlayIcon,
    TrashIcon,
    NoteIndicator: { ...NoteIndicator, name: 'NoteIndicator' },
    ActionLoader,
  },
  props: {
    bookmark: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      video: null,
      isDeletingBookmark: true,
    }
  },
  computed: {
    noteLabel() {
      if (!this.bookmark.notes) return ''
      return `Note: ${this.bookmark.notes}`
    },
    videoImgUrl() {
      return `${CONSTANTS.JW_PLAYER.MEDIA_URL}${this.bookmark.video_details?.jwplayer_id}/poster.jpg?width=480`
    },
    getBookmarkPoint() {
      const bookmarkPoint = this.bookmark.time
      const duration_in_seconds = this.bookmark.video_details
        .duration_in_seconds
      return Math.floor((100 / duration_in_seconds) * bookmarkPoint)
    },
    getBookmarkTimeLabel() {
      const { time } = this.bookmark
      return this.$duration.durationFromSecs(time, 'seconds')
    },
  },
  methods: {
    async openBookmark() {
      const { course, video, time } = this.bookmark
      this.$store.dispatch('content/setPlayingVideoId', video)
      this.$store.dispatch('content/setPlayingVideoTime', time)
      this.$router.push(this.$buildContentLink('course', course))
    },
    async deleteBookmark() {
      this.isDeletingBookmark = true
      this.$emit('delete-bookmark', this.bookmark?.id)
    },
  },
  created() {
    this.isDeletingBookmark = false
  },
}
</script>

<style lang="scss" scoped>
.bookmarks {
  &__progress-wrapper {
    position: relative;
  }
  &__progress-point {
    position: absolute;
    border-radius: 50%;
    width: 9px;
    height: 9px;
    background: var(--pale-yellow);
    top: 50%;
    transform: translateY(-50%);
  }
  &__course-container {
    position: relative;
    cursor: pointer;
    color: var(--white);
    display: flex;
    flex-direction: column;
    margin-bottom: 10px;
    background: var(--glass-mid-gray);
    border-radius: 5px;
    max-width: 769px;
    @include mobile {
      flex-direction: row;
    }
    &:hover {
      background: var(--glass-extra-light);
      .dashboard__icon--play {
        border-color: var(--white);
      }
    }
  }
  &__preview-container {
    position: relative;
    height: 105px;
    @include mobile {
      flex: 0 0 143px;
      height: auto;
    }
  }
  &__preview-img {
    width: 100%;
    height: 100%;
    max-height: 100px;
    object-fit: cover;
    filter: brightness(40%);
  }
  &__preview-icon {
    position: absolute;
    top: 50px;
    left: 50%;
    width: 18px;
    transform: translate(-50%, -50%);
  }
  &__preview-time {
    position: absolute;
    text-transform: none;
    z-index: 10;
    top: 5px;
    left: 5px;
  }
  &__course-icon {
    height: 20px;
  }
  &__course-title {
    font-size: rem(18px);
    margin: 0;
    width: 210px;
  }
  &__details {
    width: 100%;
    display: flex;
    flex-direction: column-reverse;
    justify-content: space-between;
    padding: 20px;
    @include mobile {
      flex-direction: column;
    }
  }
  &__video-title {
    color: var(--gray);
    font-size: 1rem;
  }
  &__bottom-container {
    display: flex;
    align-items: flex-start;
  }
  &__notes-wrapper {
    display: flex;
    align-items: top;
    margin-bottom: 20px;
    @include mobile {
      margin-top: 15px;
      margin-bottom: 0;
    }
  }
  &__notes-label {
    margin-left: 10px;
  }
  &__notes-icon {
    path {
      fill: var(--pale-yellow);
      &:last-child {
        fill: var(--light-yellow);
      }
    }
    height: 24px;
  }
  &__icons-wrapper {
    display: none;
    margin-left: auto;
    margin-top: 15px;
    @include mobile {
      display: flex;
    }
    &--mobile {
      display: flex;
      margin-left: 20px;
      @include mobile {
        display: none;
      }
    }
  }
  &__note {
    margin-bottom: 20px;
    word-wrap: break-word;
  }
  &__progress-container {
    display: flex;
    align-items: center;
    @include mobile {
      display: block;
    }
  }
  &__error-wrapper {
    display: flex;
    justify-content: center;
  }
  &__error {
    font-family: var(--font-bold);
    color: var(--white);
  }
}
</style>
